/**
 * Insights Input Component
 *
 * Input field for sending messages to the Insights assistant.
 */

import React, { memo, useCallback, useRef } from 'react';
import { View, Text, TextInput, Pressable, Keyboard } from 'react-native';
import Animated, { useAnimatedStyle, withSpring } from 'react-native-reanimated';
import { Send, Lightbulb } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';
import type { InsightsInputProps } from '@/lib/startup-insights/types';

function InsightsInputComponent({
  value,
  onChangeText,
  onSubmit,
  disabled = false,
  placeholder = 'Ask how to improve...',
  autoFocus = false,
}: InsightsInputProps) {
  const colors = useThemeColors();
  const inputRef = useRef<TextInput>(null);
  const canSubmit = value.trim().length > 0 && !disabled;

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      onSubmit();
      Keyboard.dismiss();
    }
  }, [canSubmit, onSubmit]);

  // Animated button style
  const buttonStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: withSpring(canSubmit ? 1 : 0.9, {
            damping: 15,
            stiffness: 200,
          }),
        },
      ],
      opacity: withSpring(canSubmit ? 1 : 0.5),
    };
  }, [canSubmit]);

  return (
    <View
      className="px-4 py-3 border-t"
      style={{ backgroundColor: colors.surface.primary, borderTopColor: colors.border.light }}
    >
      {/* Input row */}
      <View className="flex-row items-end gap-2">
        {/* AI indicator */}
        <View
          className="w-8 h-8 rounded-full items-center justify-center mb-1 shrink-0"
          style={{ backgroundColor: colors.amber.light }}
        >
          <Lightbulb size={16} color={colors.amber.main} />
        </View>

        {/* Input field */}
        <View
          className="flex-1 rounded-2xl border"
          style={{ backgroundColor: colors.surface.tertiary, borderColor: colors.border.light }}
        >
          <TextInput
            ref={inputRef}
            value={value}
            onChangeText={onChangeText}
            onSubmitEditing={handleSubmit}
            placeholder={placeholder}
            placeholderTextColor={colors.text.tertiary}
            multiline
            maxLength={2000}
            autoFocus={autoFocus}
            editable={!disabled}
            returnKeyType="send"
            blurOnSubmit={false}
            className={cn('px-4 py-3 text-base max-h-32', disabled && 'opacity-50')}
            style={{ textAlignVertical: 'center', color: colors.text.primary }}
          />
        </View>

        {/* Send button */}
        {/* The animated view carries ONLY the transform; the 44pt box is a
            plain View around it. Handing layout to a reanimated node beside an
            animated style is how a control silently measures zero (CLAUDE.md,
            pressable_sizing). Was 40pt — under the touch target. */}
        <Animated.View style={[buttonStyle, { marginBottom: 4 }]}>
          <View
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              overflow: 'hidden',
              backgroundColor: canSubmit ? colors.amber.main : colors.surface.tertiary,
            }}
          >
            <Pressable
              onPress={handleSubmit}
              disabled={!canSubmit}
              accessibilityRole="button"
              accessibilityLabel="Send message"
              accessibilityState={{ disabled: !canSubmit }}
              style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            >
              <Send
                size={18}
                color={canSubmit ? colors.text.inverse : colors.text.tertiary}
                style={{ marginLeft: 2 }}
              />
            </Pressable>
          </View>
        </Animated.View>
      </View>

      {/* Character count */}
      {value.length > 1500 && (
        <View className="mt-1 px-10">
          <Text
            className="text-xs"
            style={{ color: value.length > 1900 ? colors.red.main : colors.text.tertiary }}
          >
            {value.length}/2000
          </Text>
        </View>
      )}
    </View>
  );
}

export const InsightsInput = memo(InsightsInputComponent);
